import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  EditOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import ValuesStore, { useStore } from "../../store/values-store";
import Settings from "../../dependencies/custom/settings";
import Utilities from "../../dependencies/custom/react-utilities";
import {
  createLmsApi,
  getLmsErrorMessage,
  getLmsListItems,
} from "../../lms/api";
import {
  LMS_LIVE_SESSION_PLATFORMS,
  LMS_LIVE_SESSION_STATUSES,
  statusColor,
} from "../../lms/constants";
import {
  LmsErrorAlert,
  LmsPageHeader,
  formatDateTime,
} from "../../lms/components/LmsPrimitives";
import {
  canEditSession,
  canJoinSession,
  canOpenHost,
  hostReadinessMessage,
  normalizeLmsPermissions,
} from "../../lms/liveSessionUi";

const { Text } = Typography;
const DEFAULT_DURATION = 40;

function assignmentLabel(item) {
  const stream = item.streamName ? ` · ${item.streamName}` : "";
  return `${item.subjectName || "Subject"} · ${item.className || "Class"}${stream}`;
}

function teacherUserId(teacher) {
  return teacher.userID || teacher.userId || teacher.user?.id || teacher.id;
}

function openPendingWindow() {
  const popup = window.open("", "_blank");
  if (!popup) {
    message.warning("Please enable popups for this site, then try again.");
    return null;
  }
  if (popup) {
    popup.opener = null;
    popup.document.title = "Opening Zoom…";
    popup.document.body.textContent = "Preparing your secure Zoom link…";
  }
  return popup;
}

function sendPopupTo(popup, url) {
  let destination;
  try {
    destination = new URL(url);
  } catch (error) {
    closePopup(popup);
    message.error("Zoom returned an invalid link");
    return;
  }
  if (destination.protocol !== "https:") {
    closePopup(popup);
    message.error("Zoom returned an insecure link");
    return;
  }
  if (popup && !popup.closed) {
    popup.location.replace(destination.href);
  } else {
    message.warning("Please enable popups for this site, then try again.");
  }
}

function closePopup(popup) {
  if (popup && !popup.closed) popup.close();
}

function showFormErrors(form, response, fallback) {
  const payload = response?.data;
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const fields = Object.entries(payload)
      .filter(([name, value]) => name !== "detail" && name !== "error" && value)
      .map(([name, value]) => ({
        name,
        errors: Array.isArray(value) ? value.map(String) : [String(value)],
      }));
    if (fields.length) form.setFields(fields);
  }
  message.error(getLmsErrorMessage(response, fallback));
}

function LiveSessionsTable({
  api,
  rows,
  loading,
  pagination,
  onPage,
  onRefresh,
  onEdit,
  onDetail,
  actions,
}) {
  const [busy, setBusy] = useState({});
  const [passcodes, setPasscodes] = useState({});

  const act = async (record, action, fallback, popup) => {
    setBusy((current) => ({ ...current, [record.id]: action }));
    let response;
    try {
      if (action === "host") {
        response = record.external_id
          ? { ok: true }
          : await api.liveSessions.start(record.id);
        if (response?.ok)
          response = await api.liveSessions.joinUrl(record.id, "host");
      } else if (action === "join")
        response = await api.liveSessions.joinUrl(record.id, "participant");
      else if (action === "recording")
        response = await api.liveSessions.recording(record.id);
      else if (action === "refresh")
        response = await api.liveSessions.get(record.id);
      else response = await api.liveSessions.end(record.id);
    } catch (error) {
      closePopup(popup);
      message.error(fallback);
      setBusy((current) => ({ ...current, [record.id]: null }));
      return;
    }
    setBusy((current) => ({ ...current, [record.id]: null }));

    if (!response?.ok) {
      closePopup(popup);
      message.error(getLmsErrorMessage(response, fallback));
      return;
    }
    if (action === "end") {
      message.success("Live session ended");
      onRefresh();
      return;
    }
    if (action === "refresh") {
      message.success("LMS status reloaded");
      onRefresh(response.data);
      return;
    }
    if (response.data?.passcode) {
      setPasscodes((current) => ({
        ...current,
        [record.id]: response.data.passcode,
      }));
    } else if (action === "host" || action === "join") {
      setPasscodes((current) => {
        const next = { ...current };
        delete next[record.id];
        return next;
      });
    }
    const url = response.data?.joinUrl || response.data?.recordingUrl;
    if (!url) {
      closePopup(popup);
      message.info(
        action === "recording"
          ? "The recording is not ready yet"
          : "Zoom did not return a link yet",
      );
      if (action === "host") onRefresh();
      return;
    }
    sendPopupTo(popup, url);
    if (action === "host") onRefresh();
  };

  const columns = [
    {
      title: "Session",
      dataIndex: "title",
      fixed: "left",
      render: (value, record) => (
        <Button
          type="link"
          style={{ padding: 0, fontWeight: 600 }}
          onClick={() => onDetail(record)}
        >
          {value}
        </Button>
      ),
    },
    { title: "LMS session", dataIndex: "sessionTitle" },
    {
      title: "Class / subject",
      render: (_, record) => (
        <>
          <div>{record.className || "—"}</div>
          <Text type="secondary">{record.subjectName || "—"}</Text>
        </>
      ),
    },
    { title: "Teacher", dataIndex: "teacherName" },
    { title: "Scheduled", dataIndex: "scheduled_at", render: formatDateTime },
    {
      title: "Duration",
      dataIndex: "duration_minutes",
      render: (value) => `${value || DEFAULT_DURATION} min`,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value) => <Tag color={statusColor(value)}>{value}</Tag>,
    },
    {
      title: "Actions",
      fixed: "right",
      width: 330,
      render: (_, record) => {
        const isBusy = Boolean(busy[record.id]);
        const hostMessage = hostReadinessMessage(record);
        return (
          <Space wrap>
            {hostMessage && <Text type="secondary">{hostMessage}</Text>}
            {canOpenHost(record, actions.host(record.id)) && (
              <Button
                size="small"
                icon={<VideoCameraOutlined />}
                loading={busy[record.id] === "host"}
                onClick={() => {
                  const popup = openPendingWindow();
                  if (popup)
                    act(
                      record,
                      "host",
                      "Unable to open the Zoom meeting",
                      popup,
                    );
                }}
              >
                {record.external_id ? "Open as host" : "Start / host"}
              </Button>
            )}
            {canJoinSession(record, actions.join(record.id)) && (
              <Button
                size="small"
                type="primary"
                icon={<LinkOutlined />}
                loading={busy[record.id] === "join"}
                onClick={() => {
                  const popup = openPendingWindow();
                  if (popup)
                    act(
                      record,
                      "join",
                      "Unable to get the participant link",
                      popup,
                    );
                }}
              >
                Join
              </Button>
            )}
            {passcodes[record.id] && (
              <Text copyable={{ text: passcodes[record.id] }}>
                Passcode: {passcodes[record.id]}
              </Text>
            )}
            {actions.end(record.id) &&
              record.canManage &&
              record.status === "Live" && (
                <Popconfirm
                  title="End this live session?"
                  description="Participants will be disconnected from the Zoom meeting."
                  onConfirm={() =>
                    act(record, "end", "Unable to end the session")
                  }
                >
                  <Button
                    size="small"
                    danger
                    icon={<StopOutlined />}
                    disabled={isBusy}
                  >
                    End
                  </Button>
                </Popconfirm>
              )}
            {canEditSession(record, actions.edit(record.id)) && (
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={isBusy}
                onClick={() => onEdit(record)}
              >
                Edit
              </Button>
            )}
            {actions.recording(record.id) && record.status === "Completed" && (
              <Button
                size="small"
                loading={busy[record.id] === "recording"}
                onClick={() => {
                  const popup = openPendingWindow();
                  if (popup)
                    act(
                      record,
                      "recording",
                      "Unable to get the recording",
                      popup,
                    );
                }}
              >
                Recording
              </Button>
            )}
            <Button
              size="small"
              aria-label="Reload LMS status"
              icon={<ReloadOutlined />}
              loading={busy[record.id] === "refresh"}
              onClick={() =>
                act(record, "refresh", "Unable to reload LMS status")
              }
            />
          </Space>
        );
      },
    },
  ];

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={rows}
      loading={loading}
      scroll={{ x: 1400 }}
      pagination={{ ...pagination, showSizeChanger: true }}
      onChange={(next) => onPage(next.current, next.pageSize)}
      locale={{ emptyText: "No live sessions found" }}
    />
  );
}

export default function LmsLiveSessions() {
  const navigate = useNavigate();
  const api = useMemo(() => createLmsApi(navigate), [navigate]);
  const store = ValuesStore();
  const storedPermissions = store.getValue("permissions");
  const permissions = useMemo(
    () => normalizeLmsPermissions(storedPermissions),
    [storedPermissions],
  );
  const user = store.getValue("user") || {};
  const myRoles =
    useStore((state) => state.myRoles) ||
    JSON.parse(sessionStorage.getItem("myRoles") || "[]");
  const isSystemAdmin =
    Array.isArray(myRoles) && myRoles.includes("System Administrator");
  const isSuperuser = user.is_superuser === true || user.isSuperuser === true;
  const allowed = useCallback(
    (methods, url) =>
      isSuperuser || Utilities.hasPermissionV2(methods, permissions, url),
    [isSuperuser, permissions],
  );
  const withId = useCallback((url, id) => url.replace(":id", String(id)), []);
  const actions = useMemo(
    () => ({
      create: allowed(["POST"], Settings.urls.lms.liveSessions),
      edit: (id) =>
        allowed(
          ["PATCH", "PUT"],
          withId(Settings.urls.lms.liveSessionDetail, id),
        ),
      host: (id) =>
        allowed(["POST"], withId(Settings.urls.lms.liveSessionStart, id)) &&
        allowed(["GET"], withId(Settings.urls.lms.liveSessionJoinUrl, id)),
      join: (id) =>
        allowed(["GET"], withId(Settings.urls.lms.liveSessionJoinUrl, id)),
      end: (id) =>
        allowed(["POST"], withId(Settings.urls.lms.liveSessionEnd, id)),
      recording: (id) =>
        allowed(["GET"], withId(Settings.urls.lms.liveSessionRecording, id)),
      attendance: (id) =>
        allowed(
          ["POST"],
          withId(Settings.urls.lms.liveSessionBulkAttendance, id),
        ),
      hosts:
        isSystemAdmin && allowed(["GET"], Settings.urls.lms.zoomIdentities),
      provisionHost:
        isSystemAdmin &&
        allowed(["POST"], Settings.urls.lms.zoomIdentityProvision),
      deprovisionHost: (id) =>
        isSystemAdmin &&
        allowed(
          ["POST"],
          withId(Settings.urls.lms.zoomIdentityDeprovision, id),
        ),
    }),
    [allowed, isSystemAdmin, withId],
  );
  const [form] = Form.useForm();
  const selectedLmsSessionId = Form.useWatch("lmsSessionID", form);
  const [hostForm] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: undefined,
    lms_session: undefined,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [hostsLoading, setHostsLoading] = useState(false);
  const [hostPagination, setHostPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [hostModal, setHostModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deprovisioning, setDeprovisioning] = useState(false);
  const assignmentOptions = useMemo(() => {
    const selectedSession = sessions.find(
      (session) => String(session.id) === String(selectedLmsSessionId),
    );
    return assignments
      .filter((assignment) => {
        if (!selectedSession) return true;
        const assignmentTerm = assignment.termName || assignment.term;
        return (
          (!assignment.academicYear ||
            String(assignment.academicYear) ===
              String(selectedSession.academicYear)) &&
          (!assignmentTerm || assignmentTerm === selectedSession.termName)
        );
      })
      .map((assignment) => ({
        value: assignment.id,
        label: `${assignmentLabel(assignment)} · ${assignment.teacherName || "Teacher"}`,
      }));
  }, [assignments, selectedLmsSessionId, sessions]);

  const loadRows = useCallback(
    async (page = 1, pageSize = 20) => {
      setLoading(true);
      setError("");
      const response = await api.liveSessions.list({
        page,
        pageSize,
        platform: "Zoom",
        ordering: "scheduled_at",
        search: filters.search || undefined,
        status: filters.status,
        lms_session: filters.lms_session,
      });
      if (response?.ok) {
        const result = getLmsListItems(response.data);
        setRows(result.items);
        setPagination({ current: page, pageSize, total: result.count });
      } else
        setError(getLmsErrorMessage(response, "Unable to load live sessions"));
      setLoading(false);
    },
    [api, filters],
  );

  const loadDependencies = useCallback(async () => {
    const [sessionResponse, assignmentResponse] = await Promise.all([
      api.sessions.list({ page: 1, pageSize: 100, status: "Active" }),
      Utilities.fetchWithAuth(
        "GET",
        `${allowed(["GET"], Settings.urls.academics.subject_assignments) ? Settings.urls.academics.subject_assignments : Settings.urls.academics.my_subjects}?current=true&pageSize=100`,
        undefined,
        navigate,
      ),
    ]);
    if (sessionResponse?.ok)
      setSessions(getLmsListItems(sessionResponse.data).items);
    else
      setError(
        getLmsErrorMessage(
          sessionResponse,
          "Unable to load active LMS sessions",
        ),
      );
    if (assignmentResponse?.ok)
      setAssignments(
        Array.isArray(assignmentResponse.data)
          ? assignmentResponse.data
          : assignmentResponse.data?.results || [],
      );
    else
      setError(
        getLmsErrorMessage(
          assignmentResponse,
          "Unable to load subject assignments",
        ),
      );
  }, [allowed, api, navigate]);

  const loadHosts = useCallback(
    async (page = 1, pageSize = 20) => {
      if (!actions.hosts) return;
      setHostsLoading(true);
      const [response, teachersResponse] = await Promise.all([
        api.zoomIdentities.list({ page, pageSize, page_size: pageSize }),
        Utilities.fetchWithAuth(
          "GET",
          Settings.urls.auth.listStaff,
          undefined,
          navigate,
        ),
      ]);
      if (response?.ok) {
        const result = getLmsListItems(response.data);
        setHosts(result.items);
        setHostPagination({ current: page, pageSize, total: result.count });
      } else
        message.error(
          getLmsErrorMessage(response, "Unable to load Zoom hosts"),
        );
      if (teachersResponse?.ok) {
        const payload = teachersResponse.data;
        setTeachers(
          Array.isArray(payload)
            ? payload
            : payload?.results || payload?.data || [],
        );
      }
      setHostsLoading(false);
    },
    [actions.hosts, api, navigate],
  );

  useEffect(() => {
    loadDependencies();
  }, [loadDependencies]);
  useEffect(() => {
    loadRows(1, pagination.pageSize);
    // Filter changes intentionally reset the server page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.status, filters.lms_session]);

  const saveAttendance = async () => {
    const students = {};
    attendance.forEach((item) => {
      const studentId = item.studentID || item.studentId || item.student?.id;
      if (studentId)
        students[studentId] = {
          status: item.status || "Absent",
          joined_at: item.joined_at || null,
        };
    });
    if (!Object.keys(students).length)
      return message.info("There are no attendance records to update");
    setSaving(true);
    const response = await api.liveSessions.bulkAttendance(detail.id, {
      liveSessionID: detail.id,
      students,
    });
    setSaving(false);
    if (!response?.ok)
      return message.error(
        getLmsErrorMessage(response, "Unable to update attendance"),
      );
    message.success("Attendance updated");
    openDetail(detail);
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      platform: "Zoom",
      duration_minutes: DEFAULT_DURATION,
    });
    setFormOpen(true);
  };

  const openEdit = async (record) => {
    const response = await api.liveSessions.get(record.id);
    if (!response?.ok)
      return message.error(
        getLmsErrorMessage(response, "Unable to load the live session"),
      );
    const item = response.data;
    setEditing(item);
    form.setFieldsValue({
      lmsSessionID:
        item.lmsSessionId ||
        item.lmsSessionID ||
        sessions.find((session) => session.title === item.sessionTitle)?.id,
      subjectTeacherID: item.subjectTeacherId || item.subjectTeacherID,
      title: item.title,
      platform: item.platform || "Zoom",
      scheduled_at: item.scheduled_at ? dayjs(item.scheduled_at) : undefined,
      duration_minutes: item.duration_minutes || DEFAULT_DURATION,
    });
    setFormOpen(true);
  };

  const save = async (values) => {
    const body = {
      ...values,
      scheduled_at: values.scheduled_at.toISOString(),
      platform: "Zoom",
    };
    setSaving(true);
    const response = editing
      ? await api.liveSessions.update(editing.id, body)
      : await api.liveSessions.create(body);
    setSaving(false);
    if (!response?.ok)
      return showFormErrors(form, response, "Unable to save the live session");
    message.success(
      editing ? "Live session updated" : "Live session scheduled",
    );
    setFormOpen(false);
    setEditing(null);
    loadRows(1, pagination.pageSize);
  };

  const openDetail = async (record) => {
    const [response, attendanceResponse] = await Promise.all([
      api.liveSessions.get(record.id),
      api.liveSessionAttendance({
        live_session: record.id,
        page: 1,
        pageSize: 100,
      }),
    ]);
    setDetail(response?.ok ? response.data : record);
    setAttendance(
      attendanceResponse?.ok
        ? getLmsListItems(attendanceResponse.data).items
        : [],
    );
  };

  const provisionHost = async (values) => {
    setSaving(true);
    const response = await api.zoomIdentities.provision(values);
    setSaving(false);
    if (!response?.ok)
      return showFormErrors(
        hostForm,
        response,
        "Unable to provision the Zoom host",
      );
    message.success("Zoom host provisioned");
    setHostModal(false);
    hostForm.resetFields();
    loadHosts();
  };

  const deprovisionHost = async (record, mode) => {
    if (
      !isSystemAdmin ||
      !record ||
      record.hasLiveSession ||
      (mode === "delete" && deleteConfirm !== "DELETE")
    )
      return;
    setDeprovisioning(true);
    try {
      const response = await api.zoomIdentities.deprovision(
        record.id,
        mode,
        mode === "delete",
      );
      if (!response?.ok) {
        message.error(
          getLmsErrorMessage(response, `Unable to ${mode} the Zoom host`),
        );
        loadHosts(hostPagination.current, hostPagination.pageSize);
        return;
      }
      const cancelled = Number(response.data?.cancelledSessions || 0);
      const suffix = cancelled
        ? `; ${cancelled} upcoming session${cancelled === 1 ? " was" : "s were"} cancelled`
        : "";
      message.success(
        `${mode === "delete" ? "Zoom host permanently deleted" : "Zoom host deactivated"}${suffix}`,
      );
      setDeleteTarget(null);
      setDeleteConfirm("");
      loadHosts(hostPagination.current, hostPagination.pageSize);
      loadRows(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(
        getLmsErrorMessage(error, `Unable to ${mode} the Zoom host`),
      );
      loadHosts(hostPagination.current, hostPagination.pageSize);
    } finally {
      setDeprovisioning(false);
    }
  };

  const sessionTab = (
    <>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={10}>
            <Input.Search
              allowClear
              placeholder="Search live sessions"
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
            />
          </Col>
          <Col xs={24} sm={12} md={7}>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Status"
              value={filters.status}
              onChange={(value) =>
                setFilters((current) => ({ ...current, status: value }))
              }
              options={LMS_LIVE_SESSION_STATUSES.map((value) => ({
                value,
                label: value,
              }))}
            />
          </Col>
          <Col xs={24} sm={12} md={7}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              placeholder="LMS session"
              value={filters.lms_session}
              onChange={(value) =>
                setFilters((current) => ({ ...current, lms_session: value }))
              }
              options={sessions.map((session) => ({
                value: session.id,
                label: session.title,
              }))}
            />
          </Col>
        </Row>
      </Card>
      <LmsErrorAlert error={error} onClose={() => setError("")} />
      <Card>
        <LiveSessionsTable
          actions={actions}
          api={api}
          rows={rows}
          loading={loading}
          pagination={pagination}
          onPage={loadRows}
          onRefresh={(refreshed) => {
            if (refreshed?.id)
              setRows((current) =>
                current.map((item) =>
                  item.id === refreshed.id ? refreshed : item,
                ),
              );
            else loadRows(pagination.current, pagination.pageSize);
          }}
          onEdit={openEdit}
          onDetail={openDetail}
        />
      </Card>
    </>
  );

  const hostsTab = (
    <Card
      extra={
        actions.provisionHost ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setHostModal(true)}
          >
            Provision host
          </Button>
        ) : null
      }
    >
      <Table
        rowKey="id"
        loading={hostsLoading}
        dataSource={hosts}
        pagination={{ ...hostPagination, showSizeChanger: true }}
        onChange={(next) => loadHosts(next.current, next.pageSize)}
        columns={[
          { title: "Teacher", dataIndex: "teacherName" },
          { title: "Teacher email", dataIndex: "teacherEmail" },
          { title: "Zoom email", dataIndex: "zoomEmail" },
          {
            title: "Status",
            dataIndex: "status",
            render: (value) => (
              <Tag
                color={
                  value === "active"
                    ? "green"
                    : value === "pending"
                      ? "gold"
                      : "default"
                }
              >
                {value}
              </Tag>
            ),
          },
          { title: "Updated", dataIndex: "updatedAt", render: formatDateTime },
          {
            title: "Action",
            render: (_, record) =>
              actions.deprovisionHost(record.id) && (
                <Space direction="vertical" size={4}>
                  {record.hasLiveSession && (
                    <Text type="danger">
                      End “{record.liveSessionTitle || "the live session"}”
                      first.
                    </Text>
                  )}
                  <Space>
                    {record.status !== "disabled" && (
                      <Popconfirm
                        title="Deactivate this Zoom host?"
                        description="Scheduled sessions will be cancelled. The Zoom user, meeting history, and recordings will be kept. Provisioning this teacher again reactivates the account."
                        onConfirm={() => deprovisionHost(record, "deactivate")}
                        okButtonProps={{
                          disabled:
                            Boolean(record.hasLiveSession) || deprovisioning,
                        }}
                      >
                        <Button
                          danger
                          size="small"
                          disabled={
                            Boolean(record.hasLiveSession) || deprovisioning
                          }
                        >
                          Deactivate
                        </Button>
                      </Popconfirm>
                    )}
                    <Button
                      danger
                      size="small"
                      disabled={
                        Boolean(record.hasLiveSession) || deprovisioning
                      }
                      onClick={() => {
                        setDeleteTarget(record);
                        setDeleteConfirm("");
                      }}
                    >
                      Permanently delete
                    </Button>
                  </Space>
                </Space>
              ),
          },
        ]}
      />
    </Card>
  );

  return (
    <div>
      <LmsPageHeader
        title="Zoom live sessions"
        description="Schedule classes, securely open Zoom links, and review recordings and attendance."
        extra={[
          <Button
            key="refresh"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => {
              loadDependencies();
              loadRows(pagination.current, pagination.pageSize);
              if (actions.hosts)
                loadHosts(hostPagination.current, hostPagination.pageSize);
            }}
          >
            Refresh
          </Button>,
          actions.create && (
            <Button
              key="new"
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
            >
              Schedule session
            </Button>
          ),
        ].filter(Boolean)}
      />
      {actions.hosts ? (
        <Tabs
          defaultActiveKey="sessions"
          onChange={(key) => {
            if (key === "hosts") loadHosts();
          }}
          items={[
            { key: "sessions", label: "Live sessions", children: sessionTab },
            { key: "hosts", label: "Zoom hosts", children: hostsTab },
          ]}
        />
      ) : (
        sessionTab
      )}

      <Modal
        title={editing ? "Edit live session" : "Schedule live session"}
        open={formOpen}
        onCancel={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save}>
          <Form.Item
            name="lmsSessionID"
            label="LMS session"
            rules={[{ required: true, message: "Select an LMS session" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={sessions.map((session) => ({
                value: session.id,
                label: session.title,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="subjectTeacherID"
            label="Subject assignment"
            rules={[{ required: true, message: "Select a subject assignment" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={assignmentOptions}
            />
          </Form.Item>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, whitespace: true }]}
          >
            <Input maxLength={255} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={15}>
              <Form.Item
                name="scheduled_at"
                label="Scheduled date and time"
                rules={[{ required: true }]}
              >
                <DatePicker
                  showTime
                  format="DD MMM YYYY HH:mm"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item
                name="duration_minutes"
                label="Duration"
                extra="Zoom Basic: up to 40 minutes. Licensed hosts: up to 1,800 minutes."
                rules={[{ required: true }]}
              >
                <InputNumber
                  min={1}
                  max={1800}
                  addonAfter="min"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="platform" hidden>
            <Select
              options={LMS_LIVE_SESSION_PLATFORMS.map((value) => ({
                value,
                label: value,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={detail?.title || "Live session"}
        open={Boolean(detail)}
        onClose={() => {
          setDetail(null);
          setAttendance([]);
        }}
        width={620}
      >
        {detail && (
          <>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Status">
                <Tag color={statusColor(detail.status)}>{detail.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="LMS session">
                {detail.sessionTitle || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Teacher">
                {detail.teacherName || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Class / subject">
                {detail.className || "—"} · {detail.subjectName || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Scheduled">
                {formatDateTime(detail.scheduled_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Started">
                {formatDateTime(detail.actual_start)}
              </Descriptions.Item>
              <Descriptions.Item label="Ended">
                {formatDateTime(detail.actual_end)}
              </Descriptions.Item>
              <Descriptions.Item label="Meeting prepared">
                {detail.external_id ? "Yes" : "No"}
              </Descriptions.Item>
            </Descriptions>
            <Typography.Title level={5} style={{ marginTop: 24 }}>
              Attendance
            </Typography.Title>
            <Table
              size="small"
              rowKey={(record) =>
                record.id || record.studentID || record.studentId
              }
              pagination={false}
              dataSource={attendance}
              columns={[
                {
                  title: "Student",
                  dataIndex: "studentName",
                  render: (value, record) =>
                    value || record.student?.name || "Student",
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  render: (value, record, index) =>
                    actions.attendance(detail.id) && detail.canManage ? (
                      <Select
                        size="small"
                        value={value || "Absent"}
                        options={[
                          { value: "Present", label: "Present" },
                          { value: "Absent", label: "Absent" },
                        ]}
                        onChange={(status) =>
                          setAttendance((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index ? { ...item, status } : item,
                            ),
                          )
                        }
                      />
                    ) : (
                      <Tag color={value === "Present" ? "green" : "default"}>
                        {value || "Absent"}
                      </Tag>
                    ),
                },
                {
                  title: "Joined",
                  dataIndex: "joined_at",
                  render: formatDateTime,
                },
              ]}
              locale={{ emptyText: "No attendance records yet" }}
            />
            {actions.attendance(detail.id) &&
              detail.canManage &&
              attendance.length > 0 && (
                <Button
                  type="primary"
                  loading={saving}
                  onClick={saveAttendance}
                  style={{ marginTop: 12 }}
                >
                  Save attendance
                </Button>
              )}
          </>
        )}
      </Drawer>

      <Modal
        title="Permanently delete Zoom host?"
        open={Boolean(deleteTarget)}
        onCancel={() => {
          setDeleteTarget(null);
          setDeleteConfirm("");
        }}
        onOk={() => deprovisionHost(deleteTarget, "delete")}
        confirmLoading={deprovisioning}
        okButtonProps={{
          danger: true,
          disabled:
            deleteConfirm !== "DELETE" ||
            Boolean(deleteTarget?.hasLiveSession) ||
            deprovisioning,
        }}
        okText="Delete permanently"
        destroyOnClose
      >
        <Alert
          type="error"
          showIcon
          message="This permanently removes the Zoom user, meetings, and cloud recordings. This cannot be undone."
          description={
            deleteTarget?.hasLiveSession
              ? `End “${deleteTarget.liveSessionTitle || "the live session"}” first.`
              : "Any scheduled sessions will be cancelled."
          }
          style={{ marginBottom: 16 }}
        />
        <Text>
          Type DELETE to confirm removing{" "}
          {deleteTarget?.teacherName || "this host"}.
        </Text>
        <Input
          autoComplete="off"
          value={deleteConfirm}
          onChange={(event) => setDeleteConfirm(event.target.value)}
          style={{ marginTop: 8 }}
        />
      </Modal>

      <Modal
        title="Provision Zoom host"
        open={hostModal}
        onCancel={() => setHostModal(false)}
        onOk={() => hostForm.submit()}
        confirmLoading={saving}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          message="The teacher's account email is prefilled when available. You can edit it or leave it blank to use the account email."
          style={{ marginBottom: 16 }}
        />
        <Form form={hostForm} layout="vertical" onFinish={provisionHost}>
          <Form.Item
            name="teacherID"
            label="Teacher"
            rules={[{ required: true, message: "Select a teacher" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select teacher"
              onChange={(teacherID) => {
                const teacher = teachers.find(
                  (item) => String(teacherUserId(item)) === String(teacherID),
                );
                hostForm.setFieldValue("email", teacher?.email || undefined);
              }}
              options={teachers.map((teacher) => {
                const name =
                  teacher.full_name ||
                  teacher.fullName ||
                  teacher.name ||
                  teacher.username ||
                  [teacher.first_name, teacher.last_name]
                    .filter(Boolean)
                    .join(" ") ||
                  teacher.email ||
                  `User ${teacher.id}`;
                return {
                  value: teacherUserId(teacher),
                  label: teacher.email ? `${name} · ${teacher.email}` : name,
                };
              })}
            />
          </Form.Item>
          <Form.Item
            name="email"
            label="Zoom email"
            rules={[{ type: "email" }]}
          >
            <Input type="email" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
